import { GraphQLClient, request } from 'graphql-request';
import { GetServerSidePropsContext } from 'next';
import { destroyCookie, parseCookies } from 'nookies';

import { APPLICATION_VERSION, GRAPHQL_ENDPOINT, LAST_ORDER_COOKIE_NAME, SHOPPING_CART_COOKIE_NAME } from '../constants';
import {
  LanguageEnum,
  Maybe,
  OrderObjectType,
  WoytiOrganisationObjectType,
  WoytiReservationObjectType,
  WoytiShopObjectType,
} from '../generated/graphql';
import { captureException } from '../pages/_app';
import { isValidObjectId } from './objectid';
import { serverRedirect } from './redirect';

export function getLastOrderCookieName(organisationPathname: string = '') {
  return `${LAST_ORDER_COOKIE_NAME}-${organisationPathname}`;
}

const organisationQuery = `
query OrganisationQuery($pathname: String!) {
  getOrganisationByWoytiPathname(pathname: $pathname) {
    _id
    name
    brandColor
    logoUrl
    pathname
    vatNumber
    companyName
  }
}
`;

export async function getOrganisation(
  ctx: GetServerSidePropsContext
): Promise<Maybe<{ organisation: WoytiOrganisationObjectType; graphqlClient: GraphQLClient }>> {
  let { query } = ctx;
  let organisationPathName = query.organisation;

  let data = await request<{
    getOrganisationByWoytiPathname: Maybe<WoytiOrganisationObjectType>;
  }>(GRAPHQL_ENDPOINT, organisationQuery, { pathname: organisationPathName });

  let organisation = data.getOrganisationByWoytiPathname;
  if (!organisation) {
    console.log(`No organisation found for pathname: ${organisationPathName}`);
    return null;
  } else {
    let graphqlClient = new GraphQLClient(GRAPHQL_ENDPOINT, {
      headers: {
        organisation: organisation._id,
      },
    });

    return {
      organisation,
      graphqlClient,
    };
  }
}

const ReservationFragment = `
fragment productFields on ProductObjectType {
	_id
	id
	imageUrl
	name {
		language
		value
	}
	description {
		language
		value
	}
	allergens
	hasNutritionalInformation
	nutritionalInformation {
		energy
		fat
		saturatedFat
		cholesterol
		carbohydrate
		fiber
		sugars
		addedSugars
		protein
		salt
	}
	unitType
}

fragment reservationFields on WoytiReservationObjectType {
  _id
  id
  status
  isVendingReservation
  vendingProducts {
		product {
			...productFields
			includedProducts {
			  product {
			  	...productFields
			  }
			  amount
		  }
	  }
	  amount
  }
	vendingTotalPrice
	vendingDiscount
}
`;

export const ShopFragment = `
fragment shopFields on WoytiShopObjectType {
  _id
  name
  phonenumber
  email
  vendingPickupTime
  woytiReservationsCount
  address {
	  street
	  streetNumber
	  zipcode
	  city
	  state
	  country
  }
  organisation {
    _id
    pathname
    logoUrl
  }
  webshopUrl
}
`;

const serviceLocationQuery = `
${ReservationFragment}

${ShopFragment}

query ShopQuery($id: ObjectId!, $shoppingCartItems: [ObjectId!]!) {
  getWoytiShopById(id: $id) {
    ...shopFields
  }

  getWoytiReservationsByShopId(shopId: $id, shouldReturnSoldReservations: true) {
    ...reservationFields
  }

  getWoytiShoppingCart(items: $shoppingCartItems, shopId: $id) {
    ...reservationFields
  }
}
`;

const getOrderQuery = `
query GetOrder($id: ObjectId!, $token: String!) {
  getOrderByTokenAndId(id: $id, token: $token) {
    _id
    id
    status
    shopId
    orderLines {
      description
      totalPrice
      discountPercentage
    }
    pincode
    totalPrice
    orderToken
    payLink
    createdAt
    reservations {
      _id
      status
      pickupDateTime
      updatedStatusAt
      boxes {
        lockerId
        lockerName
        boxId
      }
    }
  }
}
`;

export async function getOrganisationAndShop(
  ctx: GetServerSidePropsContext,
  shopId: string | Array<string>
): Promise<
  Maybe<{
    organisation: WoytiOrganisationObjectType;
    shop: WoytiShopObjectType;
    graphqlClient: GraphQLClient;
    reservations: Array<WoytiReservationObjectType>;
    shoppingCart: Array<WoytiReservationObjectType>;
    order: OrderObjectType | null;
  }>
> {
  let organisationData = await getOrganisation(ctx);
  shopId = Array.isArray(shopId) ? shopId[0] : shopId;

  if (!isValidObjectId(shopId)) {
    serverRedirect(`/${ctx.locale || ''}`, ctx);
    throw new Error('Invalid Shop ID');
  }

  let cookies = parseCookies(ctx);
  let shoppingCart = [];
  if (cookies[SHOPPING_CART_COOKIE_NAME]) {
    try {
      let parsed = JSON.parse(cookies[SHOPPING_CART_COOKIE_NAME]);
      if (parsed.version === APPLICATION_VERSION) {
        let items = parsed.items.filter((reservationId) => {
          return typeof reservationId === 'string' && isValidObjectId(reservationId);
        });
        shoppingCart = items;
      }
    } catch (err) {
      // do nothing...
    }
  }

  if (!organisationData) {
    return null;
  } else {
    let orderId: string | null = null;
    let orderToken: string | null = null;
    let lastOrderCookieName = getLastOrderCookieName(organisationData.organisation?.pathname);
    let lastOrderCookie = cookies[lastOrderCookieName];
    if (ctx.query.orderId && ctx.query.orderToken) {
      orderId = ctx.query.orderId as string;
      orderToken = ctx.query.orderToken as string;
    } else if (lastOrderCookie) {
      try {
        let parsed = JSON.parse(lastOrderCookie);
        if (parsed.version === APPLICATION_VERSION) {
          orderId = parsed.orderId;
          orderToken = parsed.orderToken;
        }
      } catch (err) {
        // do nothing...
      }
    }

    let graphqlClient = organisationData.graphqlClient;
    let data = await graphqlClient.request<{
      getWoytiShopById: WoytiShopObjectType;
      getWoytiReservationsByShopId: Array<WoytiReservationObjectType>;
      getWoytiShoppingCart: Array<WoytiReservationObjectType>;
    }>(serviceLocationQuery, {
      id: shopId,
      shoppingCartItems: shoppingCart,
    });

    if (!data || !data.getWoytiShopById) {
      serverRedirect(`/${ctx.locale || ''}`, ctx);
      throw new Error('Shop not found');
    }

    let order: OrderObjectType | null = null;
    if (orderId && isValidObjectId(orderId) && orderToken) {
      try {
        let data = await graphqlClient.request<{
          getOrderByTokenAndId?: OrderObjectType;
        }>(getOrderQuery, {
          id: orderId,
          token: orderToken,
        });

        // Expired order
        order = data.getOrderByTokenAndId ?? null;
      } catch (err) {
        await captureException(err);
      }

      if (!order) {
        destroyCookie(ctx, lastOrderCookieName);
      }
    }

    return {
      organisation: organisationData.organisation,
      graphqlClient,
      order,
      shop: data.getWoytiShopById,
      reservations: data.getWoytiReservationsByShopId,
      shoppingCart: data.getWoytiShoppingCart,
    };
  }
}

const CreateOrderMutation = `
mutation CreateOrderMutation(
  $firstName: String!
  $lastName: String!
  $email: String!
  $language: LanguageEnum!
  $items: [ObjectId!]!
  $shopId: ObjectId!
) {
	createWoytiOrder(
    firstName: $firstName
    lastName: $lastName
    email: $email
    language: $language
    items: $items
    shopId: $shopId
  ) {
    _id
    orderToken
    payLink
  }
}
`;

export async function createOrder({
  firstName,
  lastName,
  email,
  language,
  items,
  shopId,
  organisationId,
}: {
  firstName: string;
  lastName: string;
  email: string;
  language: LanguageEnum;
  items: Array<string>;
  shopId: string;
  organisationId: string;
}) {
  if (!isValidObjectId(shopId)) {
    throw new Error('Invalid Shop ID');
  }

  let graphqlClient = new GraphQLClient(GRAPHQL_ENDPOINT, {
    headers: {
      organisation: organisationId,
    },
  });

  let data = await graphqlClient.request<{
    createWoytiOrder: OrderObjectType;
  }>(CreateOrderMutation, {
    firstName,
    lastName,
    email,
    language,
    items,
    shopId,
  });

  return data.createWoytiOrder;
}

const CancelOrderMutation = `
mutation CancelOrderMutation(
  $id: ObjectId!
  $token: String!
) {
	cancelOrderUsingToken(
    id: $id
    token: $token
  ) {
    _id
  }
}
`;

export async function cancelOrder({
  orderId,
  orderToken,
  organisationId,
}: {
  orderId: string;
  orderToken: string;
  organisationId: string;
}) {
  if (!isValidObjectId(orderId)) {
    throw new Error('Invalid Order ID');
  }

  let graphqlClient = new GraphQLClient(GRAPHQL_ENDPOINT, {
    headers: {
      organisation: organisationId,
    },
  });

  let data = await graphqlClient.request<{
    cancelOrderUsingToken: OrderObjectType;
  }>(CancelOrderMutation, {
    id: orderId,
    token: orderToken,
  });

  return data.cancelOrderUsingToken;
}
